
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { recipeService } from '@/services/recipe';
import { Recipe } from '@/types/recipe';
import { RecipeImageService } from '@/services/recipeImageService';
import { RECIPES, getEntityKey } from '@/constants/queryKeys';

export const useRecipeForm = (recipe?: Recipe, onRecipeUpdated?: () => void) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isProcessingImage, setIsProcessingImage] = useState(false);

  const handleSubmit = async (formData: Partial<Recipe>) => {
    if (!recipe?.id) {
      toast.error('Recipe ID is required for editing');
      return;
    }

    try {
      setIsSubmitting(true);
      
      let updatedData = {
        ...formData,
        updatedAt: new Date().toISOString()
      };

      // Check if image URL has changed and needs processing
      const newImageUrl = formData.image_url;
      const newUserImageUrl = formData.user_image_url;
      const currentImageUrl = recipe.image_url;
      const currentUserImageUrl = recipe.user_image_url;
      
      // Only process external image URLs (not user uploads from forkast_user bucket)
      const needsImageProcessing = newImageUrl && 
        newImageUrl !== currentImageUrl && 
        newImageUrl !== currentUserImageUrl &&
        !newImageUrl.includes('forkast_user') &&
        RecipeImageService.isValidImageUrl(newImageUrl);
      
      if (needsImageProcessing) {
        setIsProcessingImage(true);
        toast.info('Processing recipe image...');
        
        const imageResult = await RecipeImageService.processRecipeImage(recipe.id, newImageUrl);
        
        if (imageResult.success && imageResult.storage_image_url) {
          updatedData = {
            ...updatedData,
            storage_image_url: imageResult.storage_image_url,
            storage_image_path: imageResult.storage_image_path || null
          };
          toast.success('Recipe image processed successfully!');
        } else {
          toast.error(imageResult.error || 'Failed to process image');
          // Continue with recipe update even if image processing fails
        }
        
        setIsProcessingImage(false);
      }

      await recipeService.updateRecipe(recipe.id, updatedData);
      
      // Invalidate React Query cache to show updated data
      queryClient.invalidateQueries({ queryKey: getEntityKey(RECIPES, recipe.id) });
      queryClient.invalidateQueries({ queryKey: [RECIPES] });
      
      toast.success('Recipe updated successfully!');
      
      if (onRecipeUpdated) {
        onRecipeUpdated();
      } else {
        navigate(`/recipe/${recipe.id}`);
      }
    } catch (error) {
      console.error('Error updating recipe:', error);
      toast.error('Failed to update recipe');
    } finally {
      setIsSubmitting(false);
      setIsProcessingImage(false);
    }
  };

  const handleDelete = async () => {
    if (!recipe?.id) {
      toast.error('Recipe ID is required for deletion');
      return;
    }

    try {
      setIsSubmitting(true);
      
      await recipeService.deleteRecipe(recipe.id);
      
      // Invalidate React Query cache after deletion
      queryClient.invalidateQueries({ queryKey: getEntityKey(RECIPES, recipe.id) });
      queryClient.invalidateQueries({ queryKey: [RECIPES] });
      
      toast.success('Recipe deleted successfully!');
      navigate('/recipe-collection');
    } catch (error) {
      console.error('Error deleting recipe:', error);
      toast.error('Failed to delete recipe');
    } finally {
      setIsSubmitting(false);
    }
  };

  return {
    isSubmitting,
    isProcessingImage,
    handleSubmit,
    handleDelete
  };
};
